import { Box, Typography, useTheme } from '@mui/material';
import { Scenarios } from '@gql/base-types';
import ScenarioTile from '@components/Timeline/Tile/ScenarioTile';
import { getTimingMove, hasRideMove, isConciergePickupScenario, isConciergeReturnScenario, isConciergeScenario, type Scenario as SdkScenario } from '@hopdrive/sdk-appointments';
interface ScheduleScenarioBlockProps {
  scenario: Scenarios;
  leftPosition: number; // pixels from start of day
  onClick: (scenario: Scenarios) => void;
  zoom?: number;
}


const ScheduleScenarioBlock = ({
  scenario,
  leftPosition,
  onClick,
  zoom = 4,
}: ScheduleScenarioBlockProps) => {
  const theme = useTheme();

  // Check if we need to render ghost travel blocks
  const shouldShowTravelBlock =
    isConciergeScenario(scenario.scenario_key) && !hasRideMove(scenario);

  // Get travel duration for ghost block
  let travelDurationMin = 0;
  let travelWidthPx = 0;

  if (shouldShowTravelBlock) {
    const timingMove = getTimingMove(scenario as unknown as SdkScenario);
    if (timingMove?.lane) {
      // Ghost blocks show estimated travel time (not actual move duration):
      // - concierge-pu: Ghost "Travel" block BEFORE = travel TO customer (inverse duration)
      // - concierge-ret: Ghost "Return" block AFTER = return TO dealer (forward duration)
      const laneDuration =
        isConciergePickupScenario(scenario.scenario_key)
          ? timingMove.lane.inverse?.duration_sec || timingMove.lane.duration_sec
          : timingMove.lane.duration_sec; // concierge-ret uses forward duration for return trip

      if (laneDuration) {
        travelDurationMin = Math.round(laneDuration / 60);
        travelWidthPx = travelDurationMin * zoom;
      }
    }
  }

  // Adjust leftPosition ONLY for concierge-pu to account for ghost travel block rendered before appointment
  // The ghost block is visual-only but rendered in flexbox before the tile, so we need to shift the container left
  // concierge-ret doesn't need adjustment because the blue tile IS the travel to customer, Return block is AFTER
  // concierge-loaner-pu doesn't have ghost blocks at all (uses real moves)
  const adjustedLeftPosition =
    isConciergePickupScenario(scenario.scenario_key) && shouldShowTravelBlock && travelWidthPx > 0
      ? leftPosition - travelWidthPx
      : leftPosition;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${adjustedLeftPosition}px`,
        top: 8,
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* Ghost travel block BEFORE appointment (concierge-pu only) */}
      {/* For concierge-pu: driver travels TO customer before the appointment/pickup */}
      {shouldShowTravelBlock && isConciergePickupScenario(scenario.scenario_key) && travelWidthPx > 0 && (
        <Box
          sx={{
            width: `${travelWidthPx}px`,
            height: '64px',
            background: `repeating-linear-gradient(
              45deg,
              ${theme.palette.warning.light}40,
              ${theme.palette.warning.light}40 10px,
              ${theme.palette.warning.main}20 10px,
              ${theme.palette.warning.main}20 20px
            )`,
            borderRadius: '8px',
            border: `2px solid ${theme.palette.warning.main}60`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '4px',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 600,
              color: theme.palette.warning.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              userSelect: 'none',
            }}
          >
            Travel
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: theme.palette.warning.dark,
              userSelect: 'none',
              lineHeight: 1,
            }}
          >
            {travelDurationMin} min
          </Typography>
        </Box>
      )}

      {/* Main scenario tile */}
      <ScenarioTile scenario={scenario} onClick={() => onClick(scenario)} size="medium" zoom={zoom} />

      {/* Ghost travel block AFTER appointment (concierge-ret only) */}
      {/* For concierge-ret: driver returns to dealer AFTER dropping off the car */}
      {shouldShowTravelBlock && isConciergeReturnScenario(scenario.scenario_key) && travelWidthPx > 0 && (
        <Box
          sx={{
            width: `${travelWidthPx}px`,
            height: '64px',
            background: `repeating-linear-gradient(
              45deg,
              ${theme.palette.warning.light}40,
              ${theme.palette.warning.light}40 10px,
              ${theme.palette.warning.main}20 10px,
              ${theme.palette.warning.main}20 20px
            )`,
            borderRadius: '8px',
            border: `2px solid ${theme.palette.warning.main}60`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '4px',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 600,
              color: theme.palette.warning.dark,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              userSelect: 'none',
            }}
          >
            Return
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: theme.palette.warning.dark,
              userSelect: 'none',
              lineHeight: 1,
            }}
          >
            {travelDurationMin} min
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleScenarioBlock;
